import { formatCurrency } from "@/lib/utils";

interface PaymentBreakdownProps {
  baseAmount: number;
  markupAmount: number;
  platformFeeAmount: number;
  totalAmount: number;
  showDetails?: boolean;
  userRole?: string;
}

export function PaymentBreakdown({
  baseAmount,
  markupAmount,
  platformFeeAmount,
  totalAmount,
  showDetails = false,
  userRole
}: PaymentBreakdownProps) {
  // Format the amounts for display
  const formattedBaseAmount = formatCurrency(baseAmount);
  const formattedMarkupAmount = formatCurrency(markupAmount);
  const formattedPlatformFee = formatCurrency(platformFeeAmount);
  const formattedTotalAmount = formatCurrency(totalAmount);

  // Calculate percentages for the progress bars
  const basePercentage = (baseAmount / totalAmount) * 100;
  const markupPercentage = (markupAmount / totalAmount) * 100;
  const platformFeePercentage = (platformFeeAmount / totalAmount) * 100;

  return (
    <div className="space-y-4">
      {userRole === 'guest' ? (
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium">Total Payment</span>
          <span className="text-sm font-semibold">{formattedTotalAmount}</span>
        </div>
      ) : (
        <>
          <div className="mb-2">
            <div className="flex justify-between mb-1">
              <span className="text-sm font-medium">Payment Breakdown</span>
              <span className="text-sm font-semibold">{formattedTotalAmount}</span>
            </div>

            {/* Visual payment breakdown bar */}
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 float-left rounded-l-full"
                style={{ width: `${basePercentage}%` }}
                title="Owner's share"
              />
              <div
                className="h-full bg-green-500 float-left"
                style={{ width: `${markupPercentage}%` }}
                title="Agent's markup"
              />
              <div
                className="h-full bg-amber-500 float-left rounded-r-full"
                style={{ width: `${platformFeePercentage}%` }}
                title="Platform fee"
              />
            </div>
          </div>

          {showDetails && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                  <span className="text-muted-foreground">Property Owner</span>
                </div>
                <span>{formattedBaseAmount}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                  <span className="text-muted-foreground">Agent Markup</span>
                </div>
                <span>{formattedMarkupAmount}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-amber-500 rounded-full mr-2"></div>
                  <span className="text-muted-foreground">Platform Fee </span>
                </div>
                <span>{formattedPlatformFee}</span>
              </div>
              
              <div className="h-px bg-border my-2"></div>
              
              <div className="flex justify-between items-center font-medium">
                <span>Total Payment</span>
                <span>{formattedTotalAmount}</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}